import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, FormControl, InputLabel, Select,
  MenuItem, Alert, Chip, LinearProgress, Divider, Stack, CircularProgress,
  Checkbox, FormControlLabel
} from '@mui/material';
import {
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  SystemUpdateAlt as OtaIcon,
  CheckCircle as ReadyIcon,
  HourglassEmpty as BusyIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { getSocket } from '../socket';

// OTA endpoints live at the server root (/ota/*), NOT under the /api baseURL,
// so every OTA call overrides axios baseURL to ''. The auth interceptor still runs.
const OTA = { baseURL: '' };

function FirmwareOta() {
  const [firmwareList, setFirmwareList] = useState([]);
  const [onlineDevices, setOnlineDevices] = useState([]);
  const [selectedFirmware, setSelectedFirmware] = useState('');
  const [targetDeviceId, setTargetDeviceId] = useState('');

  const [file, setFile] = useState(null);
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploading, setUploading] = useState(false);

  const [readiness, setReadiness] = useState('unknown'); // unknown | checking | ready | busy
  const [readinessReason, setReadinessReason] = useState('');
  const [starting, setStarting] = useState(false);
  const [busyBlocked, setBusyBlocked] = useState(false); // set when /ota/start returns 409
  // Interim override: the readiness check depends on STM32 firmware answering
  // CMD_GET_DEVICE_READY (0x02), which isn't shipped yet — so every device reads
  // as "busy". This lets an admin who has confirmed the device is idle skip the
  // gate in one click. Remove/undefault once the firmware reports readiness.
  const [overrideReadiness, setOverrideReadiness] = useState(false);

  const [progress, setProgress] = useState(null); // { otaStatus, message, progress }
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef();

  const refreshFirmware = useCallback(async () => {
    try {
      const res = await api.get('/ota/firmware-list', OTA);
      setFirmwareList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError('Failed to load firmware list: ' + (e.response?.data?.error || e.message));
    }
  }, []);

  const refreshDevices = useCallback(async () => {
    try {
      // Same source /ota/start targets (deviceComm wsDeviceMap of online devices).
      const res = await api.get('/device/devices/online');
      setOnlineDevices(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    refreshFirmware();
    refreshDevices();
  }, [refreshFirmware, refreshDevices]);

  // Live OTA progress broadcast by the backend as it flashes the device.
  useEffect(() => {
    const socket = getSocket();
    const onOtaStatus = (msg) => {
      if (targetDeviceId && msg?.deviceId && msg.deviceId !== targetDeviceId) return;
      setProgress({
        otaStatus: msg?.otaStatus,
        message: msg?.message,
        progress: typeof msg?.progress === 'number' ? msg.progress : null,
      });
    };
    socket.on('ota-status', onOtaStatus);
    return () => { socket.off('ota-status', onOtaStatus); };
  }, [targetDeviceId]);

  // Reset the readiness verdict whenever the target changes.
  useEffect(() => {
    setReadiness('unknown');
    setReadinessReason('');
    setBusyBlocked(false);
    setProgress(null);
  }, [targetDeviceId]);

  const handleUpload = async () => {
    if (!file) { setError('Choose a .bin firmware file first.'); return; }
    setError(''); setSuccess(''); setUploading(true);
    try {
      const buffer = await file.arrayBuffer();
      const res = await api.post('/ota/upload-firmware', buffer, {
        ...OTA,
        params: { filename: file.name, version: uploadVersion || '1.0.0' },
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      setSuccess(`Firmware "${file.name}" uploaded (${res.data?.size ?? file.size} bytes).`);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await refreshFirmware();
      if (res.data?.url) setSelectedFirmware(res.data.url);
    } catch (e) {
      setError('Upload failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setUploading(false);
    }
  };

  const checkReadiness = async () => {
    if (!targetDeviceId) { setError('Select a target device first.'); return; }
    setError(''); setReadiness('checking'); setReadinessReason('');
    try {
      const res = await api.get(`/ota/precheck/${encodeURIComponent(targetDeviceId)}`, OTA);
      if (res.data?.ready) {
        setReadiness('ready');
      } else {
        setReadiness('busy');
        setReadinessReason(res.data?.reason || 'Device did not report ready.');
      }
    } catch (e) {
      setReadiness('busy');
      setReadinessReason(e.response?.data?.reason || e.message);
    }
  };

  const startUpdate = async (force = false) => {
    if (!targetDeviceId) { setError('Select a target device first.'); return; }
    if (!selectedFirmware) { setError('Select a firmware image first.'); return; }
    setError(''); setSuccess(''); setStarting(true); setBusyBlocked(false);
    const fw = firmwareList.find(f => f.url === selectedFirmware);
    try {
      await api.post('/ota/start', {
        deviceId: targetDeviceId,
        firmwareUrl: selectedFirmware,
        version: fw?.version,
        force,
      }, OTA);
      setSuccess(force
        ? 'Forced OTA command sent. Watch progress below.'
        : 'Device is idle — OTA command sent. Watch progress below.');
      setProgress({ otaStatus: 'initiated', message: 'OTA command forwarded to device', progress: 0 });
    } catch (e) {
      if (e.response?.status === 409) {
        // Cycle-safety gate tripped: device is mid-cycle.
        setReadiness('busy');
        setReadinessReason(e.response?.data?.detail || 'Device is mid-cycle.');
        setBusyBlocked(true);
        setError(e.response?.data?.error || 'Device is busy.');
      } else {
        setError('Failed to start OTA: ' + (e.response?.data?.error || e.message));
      }
    } finally {
      setStarting(false);
    }
  };

  const readinessChip = () => {
    switch (readiness) {
      case 'checking':
        return <Chip size="small" icon={<CircularProgress size={14} />} label="Checking…" />;
      case 'ready':
        return <Chip size="small" color="success" icon={<ReadyIcon />} label="Idle — safe to update" />;
      case 'busy':
        return <Chip size="small" color="warning" icon={<BusyIcon />} label="Busy / mid-cycle" />;
      default:
        return <Chip size="small" variant="outlined" label="State unknown" />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Firmware / OTA Update</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { refreshFirmware(); refreshDevices(); }}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* 1. Upload firmware */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>1. Upload firmware image</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
            {file ? file.name : 'Choose .bin file'}
            <input type="file" hidden accept=".bin" ref={fileInputRef}
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Button>
          <TextField size="small" label="Version" placeholder="1.0.0"
            value={uploadVersion} onChange={(e) => setUploadVersion(e.target.value)} />
          <Button variant="contained" onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </Stack>
      </Paper>

      {/* 2. Select target + firmware */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>2. Select target device &amp; firmware</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Target Device</InputLabel>
            <Select value={targetDeviceId} label="Target Device"
              onChange={(e) => setTargetDeviceId(e.target.value)}>
              {onlineDevices.length === 0 ? (
                <MenuItem value="" disabled>No online devices</MenuItem>
              ) : onlineDevices.map(id => <MenuItem key={id} value={id}>{id}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Firmware Image</InputLabel>
            <Select value={selectedFirmware} label="Firmware Image"
              onChange={(e) => setSelectedFirmware(e.target.value)}>
              {firmwareList.length === 0 ? (
                <MenuItem value="" disabled>No firmware uploaded</MenuItem>
              ) : firmwareList.map(fw => (
                <MenuItem key={fw.url || fw.filename} value={fw.url}>
                  {fw.filename}{fw.version ? ` (v${fw.version})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* 3. Cycle-safety check + start */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>3. Verify device is idle, then update</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A device running its cycle will not answer the readiness probe. The server also
          re-checks readiness at start and refuses to flash a busy device unless you force it.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" onClick={checkReadiness}
            disabled={!targetDeviceId || readiness === 'checking'}>
            Check device state
          </Button>
          {readinessChip()}
        </Stack>
        {readiness === 'busy' && readinessReason && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {readinessReason}
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              Note: readiness reporting requires an STM32 firmware update that isn't deployed yet, so
              a device may show as busy even when idle. If you've confirmed it's idle, use the
              override below.
            </Typography>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          sx={{ mb: 1 }}
          control={<Checkbox checked={overrideReadiness} onChange={(e) => setOverrideReadiness(e.target.checked)} />}
          label="Device is idle — skip readiness check (required until firmware reports readiness)"
        />
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="contained" color={overrideReadiness ? 'error' : 'primary'} startIcon={<OtaIcon />}
            onClick={() => startUpdate(overrideReadiness)}
            disabled={starting || !targetDeviceId || !selectedFirmware}>
            {starting ? 'Starting…' : (overrideReadiness ? 'Update (skip check)' : 'Start Update')}
          </Button>
          {busyBlocked && !overrideReadiness && (
            <Button variant="outlined" color="error"
              onClick={() => startUpdate(true)} disabled={starting}>
              Force update anyway
            </Button>
          )}
        </Stack>
      </Paper>

      {/* 4. Progress */}
      {progress && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>OTA progress</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>{progress.otaStatus || 'status'}</b>{progress.message ? ` — ${progress.message}` : ''}
          </Typography>
          <LinearProgress
            variant={typeof progress.progress === 'number' ? 'determinate' : 'indeterminate'}
            value={typeof progress.progress === 'number' ? progress.progress : undefined}
          />
          {typeof progress.progress === 'number' && (
            <Typography variant="caption" color="text.secondary">{progress.progress}%</Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default FirmwareOta;
